/**
 * Component: Countdown
 * -----------------------------------------------------------------------------
 * Scripts to handle a simple countdown.
 *
 * - USAGE:
 *   - Can be used by adding an element with the `js-countdown="container"`
 *     attribute.
 *   - The container element needs a `data-countdown` attribute with the date in
 *     DD-MM-YYYY HH:MM:SS format.
 *   - The countdown text is added to the element with the
 *     `js-countdown="countdown"` attribute.
 *     - You could add content to the countdown element and that will display
 *       while the Javascript is loading.
 *   - Once the Javascript is initialized, a `data-countdown-state` is added to
 *     the container element. The values are `running` if the countdown is
 *     running and `ended` if the countdown has ended.
 *     - You can use these attributes in CSS to show/hide elements based on the
 *       countdown state.
 * @example
 *   <div js-countdown="container" data-countdown="31-12-2024 12:00:00">
 *     <div class="text-heading-xl" js-countdown="countdown">
 *       <!-- Countdown text added here -->
 *     </div>
 *
 *     <!-- Other elements can be added here. They can be shown/hidden with CSS -->
 *   </div>
 *
 */

/**
 * DOM Selectors.
 */
const selectors = {
  container: '[js-countdown="container"]',
  countdown: '[js-countdown="countdown"]',
}

/**
 * Create a countdown web object.
 */
export default() => {
  const nodeSelectors = {
    containers: [...document.querySelectorAll(selectors.container)],
  }

  /**
   * Initialise component.
   */
  function init() {
    if (!nodeSelectors.containers.length) {
      return
    }

    setCountdowns()
  }

  function setCountdowns() {
    nodeSelectors.containers.forEach((container) => {
      setCountdown(container)
    })
  }

  function setCountdown(container) {
    const countdownElement = container.querySelector(selectors.countdown)

    if (!countdownElement) {
      return
    }

    const endDateData = container.dataset.countdown
    const endDate = parseDate(endDateData)
    let interval = null

    updateCountdown({ container, countdownElement, endDate, interval })
    interval = setInterval(() => updateCountdown({ container, countdownElement, endDate, interval }), 1000)

  }

  function parseDate(input) {
    const parts = input.match(/(\d+)/g)

    return new Date(
      parts[2],
      parts[1] - 1,
      parts[0],
      parts[3],
      parts[4],
      parts[5],
    )
  }

  function updateCountdown({
    container,
    countdownElement,
    endDate,
    interval,
  }) {
    const now = new Date()
    const timeLeft = endDate - now

    if (timeLeft <= 0) {
      handleCountdownEnd({
        container, countdownElement, interval,
      })
      return
    }

    if (container.dataset.countdownState !== 'running') {
      container.dataset.countdownState = 'running'
    }

    updateCountdownText(timeLeft, countdownElement)
  }

  function handleCountdownEnd({ container, countdownElement, interval }) {
    // Stop interval if set
    container.dataset.countdownState = 'ended'
    updateCountdownText(0, countdownElement)
    clearInterval(interval)
  }

  function updateCountdownText(timeLeft, countdownElement) {
    const sanitizedTimeLeft = Math.max(0, timeLeft)

    const days = Math.floor(sanitizedTimeLeft / (1000 * 60 * 60 * 24))
    const hours = Math.floor((sanitizedTimeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((sanitizedTimeLeft % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((sanitizedTimeLeft % (1000 * 60)) / 1000)

    const string = `${days}d : ${hours}h : ${minutes}m : ${seconds}s`

    countdownElement.innerHTML = string
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}